import { ArrowPathIcon } from "@heroicons/react/24/outline";
import React from "react";

import { Badge, Button, Headline, Paragraph } from "@fronterahealth/frontera-ui-components";

import { EhrConnectionStatus } from "@api/graphql/types-and-hooks";
import { EHRConnectBadge } from "@components/EHRConnect/EHRConnectBadge";
import { EhrSyncGatedNotice } from "@components/EHRConnect/EhrSyncGatedNotice";
import { OnboardingShell } from "@pages/Onboarding/OnboardingShell";
import { isDirectIntegrationOrgType, isEhrConnected } from "@pages/Onboarding/ehrConnectionState";
import { useAdminData } from "@providers/AdminDataProvider";
import { useEHRConnection } from "@providers/EHRConnectionProvider";

import hiRasmusLogo from "../../../assets/ehr/hi-rasmus.png";

interface ConnectEHRStepProps {
  onNext: () => void;
  onBack: () => void;
  // Skip connecting now and continue to client import (the EHR can be connected later).
  onSkip: () => void;
  currentStep: number;
  totalSteps: number;
}

// Step 3 — Connect the clinic's EHR / data-collection platform. Presented as an
// inline full-page step; the credential entry itself reuses the existing
// EHRLoginDialog via openEHRLogin() (the heaviest, MFA-aware piece). The row's
// state reflects the live ehrConnectionStatus. Matches Figma frames 204:179 /
// 204:243 / 204:316. Passage / API-based orgs skip this step entirely (handled
// by OnboardingPage).
export const ConnectEHRStep: React.FC<ConnectEHRStepProps> = ({ onNext, onBack, onSkip, currentStep, totalSteps }) => {
  const { externalOrganizationName, organizationLogo, isHiRasmusUser, organizationType, isEhrSyncCleared } =
    useAdminData();
  const { ehrConnectionStatus, openEHRLogin } = useEHRConnection();

  // Local-only override: use the higher-res hiRasmus PNG on this step without
  // changing the app-wide organizationLogo (header badge / EHR dialog keep the SVG).
  const logoSrc = isHiRasmusUser ? hiRasmusLogo : organizationLogo;

  // Direct-integration orgs (Passage / Therapz / Hipp Health) connect via an
  // org-level API, not per-user credentials — the backend always reports them
  // CONNECTED. There's nothing to do here, so present the row as connected and
  // let the user continue (no Connect button / credential dialog).
  const isDirectIntegrationOrg = isDirectIntegrationOrgType(organizationType);

  const isConnected = isDirectIntegrationOrg || isEhrConnected(ehrConnectionStatus);
  const isConnecting =
    ehrConnectionStatus === EhrConnectionStatus.LoggingIn || ehrConnectionStatus === EhrConnectionStatus.Reconnecting;
  const isDisconnected =
    ehrConnectionStatus === EhrConnectionStatus.Disconnected ||
    ehrConnectionStatus === EhrConnectionStatus.ConnectionError ||
    ehrConnectionStatus === EhrConnectionStatus.CredentialsFailed;

  let connectionIndicator: React.ReactNode;
  if (isConnected) {
    connectionIndicator = <Badge text="Connected" appearance="success" dot />;
  } else if (isConnecting) {
    connectionIndicator = (
      <span className="flex items-center gap-1.5 text-sm text-text-tertiary">
        <ArrowPathIcon className="animate-slow-spin h-4 w-4" />
        Connecting…
      </span>
    );
  } else {
    connectionIndicator = (
      <div className="flex flex-col items-start gap-1">
        <div className="flex items-center gap-4">
          {isDisconnected && (
            <div className="flex h-6 items-center justify-center rounded-md bg-limestone-50 px-1.5 py-4 text-xs text-limestone-600">
              <EHRConnectBadge status={null} isEhrSyncCleared={isEhrSyncCleared} />
            </div>
          )}
          <Button
            // Onboarding always reads "Connect" — even for a returning, disconnected
            // user with saved credentials — to keep this step's CTA simple. (The
            // main-app EHR badge still says "Reconnect".) Run the full intro flow
            // (second arg) — "Reaching out" loading screen → consent surface →
            // dedicated login — same as the header badge; inline-error mode (third
            // arg) keeps login errors in the form. Direct-integration orgs
            // (Passage/Therapz/Hipp) never reach this button.
            text="Connect"
            variant="primary"
            buttonStyle="border"
            disabled={!isEhrSyncCleared}
            onClick={() => openEHRLogin(undefined, true, true)}
          />
        </div>
        {!isEhrSyncCleared && <EhrSyncGatedNotice />}
      </div>
    );
  }

  return (
    <OnboardingShell
      onContinue={onNext}
      onBack={onBack}
      // Once connected there's nothing to skip — and skipping wouldn't disconnect
      // them anyway — so hide "Skip for now" and let them Continue. While not
      // connected, Skip is the only way forward (Continue stays truly disabled —
      // no bypass — until the EHR connects).
      onSkip={isConnected ? undefined : onSkip}
      continueDisabled={!isConnected}
      currentStep={currentStep}
      totalSteps={totalSteps}
    >
      <Headline size="medium">Connect accounts</Headline>
      <Paragraph size="small" className="mt-2 text-text-secondary">
        Connecting your EHR lets Frontera securely sync your clients and their data, so your reports and client data
        stay in step with your system of record. That means no duplicate data entry and a faster path from intake to a
        finished report. You can disconnect your EHR at any time from your settings.
      </Paragraph>

      <div className="mt-8">
        <p className="text-base font-semibold text-text-primary">Your clinic&apos;s listed EHR</p>
        {!isConnected && (
          <Paragraph size="small" className="mt-1 text-text-secondary">
            By connecting your EHR, you agree to Frontera&apos;s{" "}
            <a
              href="https://fronterahealth.com/terms-of-use/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-interactive-primary-default underline"
            >
              Terms of use
            </a>
            {"."}
          </Paragraph>
        )}

        <div className="mt-4 flex items-center gap-4 rounded-lg border border-border-medium p-4">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-limestone-100">
            {logoSrc ? (
              <img src={logoSrc} alt="" className="h-6 w-6 object-contain" />
            ) : (
              <span className="text-sm font-semibold text-text-secondary">
                {externalOrganizationName.charAt(0) || "?"}
              </span>
            )}
          </div>
          <span className="flex-1 text-base font-medium text-text-primary">{externalOrganizationName}</span>

          {connectionIndicator}
        </div>
      </div>
    </OnboardingShell>
  );
};
